import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { ScrollArea } from "../components/ui/scroll-area";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  CheckCircle,
  Clock,
  Bell,
  LogOut,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  MessageSquare,
  Calendar,
  TrendingUp,
  ListTodo,
  LayoutDashboard,
  AlertCircle,
  Send,
  Phone,
  CalendarDays,
  CalendarRange
} from "lucide-react";
import { format, formatDistanceToNow, startOfWeek, endOfWeek, eachDayOfInterval, addWeeks, subWeeks, isSameDay, addDays } from "date-fns";
import { cn } from "../lib/utils";
import { QACallsModule } from "./QACallsModule";

export const EmployeeDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  // Calendar state
  const [calendarView, setCalendarView] = useState("monthly"); // "monthly" or "weekly"
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 })); // Start on Monday
  const [dayTasks, setDayTasks] = useState([]);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [statsRes, tasksRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/stats`, { withCredentials: true }),
        axios.get(`${API}/tasks`, { withCredentials: true }),
        axios.get(`${API}/notifications`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setTasks(tasksRes.data);
      setNotifications(notificationsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        navigate("/", { replace: true });
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch tasks for selected date
  const fetchDayTasks = useCallback(async (date) => {
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const res = await axios.get(`${API}/tasks/by-date/${dateStr}`, { withCredentials: true });
      setDayTasks(res.data);
    } catch (error) {
      console.error("Error fetching day tasks:", error);
      setDayTasks([]);
    }
  }, []);

  // Update day tasks when selected date changes
  useEffect(() => {
    fetchDayTasks(selectedDate);
  }, [selectedDate, fetchDayTasks]);

  // Get tasks for a specific date from the tasks array
  const getTasksForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return tasks.filter(task => task.task_date === dateStr);
  };

  // Format time for display (convert 24h to 12h)
  const formatTime = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Calculate end time based on start time and duration
  const getEndTime = (startTime, duration) => {
    if (!startTime || !duration) return "";
    const [hours, minutes] = startTime.split(":").map(Number);
    const totalMinutes = hours * 60 + minutes + duration;
    const endHours = Math.floor(totalMinutes / 60) % 24;
    const endMinutes = totalMinutes % 60;
    return `${String(endHours).padStart(2, "0")}:${String(endMinutes).padStart(2, "0")}`;
  };

  // WebSocket for real-time notifications
  useEffect(() => {
    if (!user?.user_id) return;

    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace(/^http/, 'ws');
    const ws = new WebSocket(`${wsUrl}/ws/${user.user_id}`);

    ws.onopen = () => {
      console.log("WebSocket connected");
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      toast.info(data.message);
      fetchData();
    };

    ws.onclose = () => {
      console.log("WebSocket disconnected");
    };

    // Ping to keep alive
    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send("ping");
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      ws.close();
    };
  }, [user?.user_id, fetchData]);

  // Logout
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // Update task status
  const handleStatusUpdate = async (taskId, newStatus) => {
    try {
      await axios.patch(`${API}/tasks/${taskId}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Task ${newStatus === "completed" ? "completed" : "updated"}`);
      fetchData();
      if (selectedTask?.task_id === taskId) {
        setSelectedTask(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  // Add note
  const handleAddNote = async () => {
    if (!noteText.trim() || !selectedTask) return;
    
    try {
      const response = await axios.post(
        `${API}/tasks/${selectedTask.task_id}/notes`,
        { text: noteText },
        { withCredentials: true }
      );
      setSelectedTask(response.data);
      setNoteText("");
      toast.success("Note added");
      fetchData();
    } catch (error) {
      toast.error("Failed to add note");
    }
  };

  // Mark notification as read
  const markNotificationRead = async (notificationId) => {
    try {
      await axios.patch(`${API}/notifications/${notificationId}/read`, {}, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error("Error marking notification read:", error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    if (filterStatus === "all") return true;
    return task.status === filterStatus;
  });

  // Open task detail
  const openTaskDetail = (task) => {
    setSelectedTask(task);
    setShowTaskDetail(true);
  };

  // Get priority color
  const getPriorityIcon = (priority) => {
    switch (priority) {
      case "high":
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case "medium":
        return <Clock className="w-4 h-4 text-amber-500" />;
      default:
        return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30" data-testid="employee-dashboard">
      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 mobile-overlay lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside className={cn(
        "fixed left-0 top-0 z-50 h-full w-64 bg-white border-r border-border transition-transform duration-300 lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-2 px-6 py-5 border-b border-border">
            <div className="w-8 h-8 rounded-lg bg-[#0066CC] flex items-center justify-center">
              <Phone className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-heading font-bold text-foreground">Lexia</span>
            <button 
              className="ml-auto lg:hidden p-1 rounded-md hover:bg-muted"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1">
            <button
              onClick={() => setActiveTab("dashboard")}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                activeTab === "dashboard" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="w-5 h-5" />
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab("tasks")}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                activeTab === "tasks" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
              data-testid="nav-my-tasks"
            >
              <ListTodo className="w-5 h-5" />
              My Tasks
            </button>
            <button
              onClick={() => setActiveTab("qa_calls")}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                activeTab === "qa_calls" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
              data-testid="nav-qa-calls"
            >
              <Phone className="w-5 h-5" />
              QA Calls
            </button>
          </nav>

          {/* User */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 px-2">
              <Avatar className="w-9 h-9">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback>{user?.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-border">
          <div className="flex items-center justify-between px-4 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <button 
                className="lg:hidden p-2 rounded-md hover:bg-muted"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-heading font-semibold text-foreground">
                  {activeTab === "qa_calls" ? "QA Calls" : `Hello, ${user?.name?.split(" ")[0]}!`}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {activeTab === "qa_calls" ? "Quality Assurance Follow-up Calls" : "Here's your task overview"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Notifications */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center notification-pulse">
                        {unreadCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <ScrollArea className="h-64">
                    {notifications.length === 0 ? (
                      <div className="py-8 text-center text-muted-foreground text-sm">
                        No notifications
                      </div>
                    ) : (
                      notifications.slice(0, 10).map((notification) => (
                        <DropdownMenuItem
                          key={notification.notification_id}
                          className={cn("flex flex-col items-start gap-1 p-3 cursor-pointer", !notification.read && "bg-primary/5")}
                          onClick={() => markNotificationRead(notification.notification_id)}
                        >
                          <p className="text-sm">{notification.message}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                          </p>
                        </DropdownMenuItem>
                      ))
                    )}
                  </ScrollArea>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback>{user?.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>{user?.name}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {user?.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin")}>
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Admin Dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-4 lg:p-8">
          {/* Dashboard Tab */}
          {activeTab === "dashboard" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="shadow-card">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">My Tasks</p>
                        <p className="text-2xl font-heading font-bold text-foreground mt-1">{stats?.total_tasks || 0}</p>
                      </div>
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <ListTodo className="w-5 h-5 text-primary" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-card">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Pending</p>
                        <p className="text-2xl font-heading font-bold text-amber-600 mt-1">{stats?.pending_tasks || 0}</p>
                      </div>
                      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-amber-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-card">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">In Progress</p>
                        <p className="text-2xl font-heading font-bold text-blue-600 mt-1">{stats?.in_progress_tasks || 0}</p>
                      </div>
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-card">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Completed</p>
                        <p className="text-2xl font-heading font-bold text-green-600 mt-1">{stats?.completed_tasks || 0}</p>
                      </div>
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Progress Card */}
              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="text-lg font-heading">My Progress</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-4xl font-heading font-bold text-foreground">{stats?.completion_rate || 0}%</span>
                      <span className="text-sm text-muted-foreground">{stats?.completed_tasks || 0} of {stats?.total_tasks || 0} tasks completed</span>
                    </div>
                    <Progress value={stats?.completion_rate || 0} className="h-3" />
                  </div>
                </CardContent>
              </Card>

              {/* Recent Tasks */}
              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="text-lg font-heading">Today's Tasks</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {tasks.filter(t => t.status !== "completed").slice(0, 5).map((task) => (
                      <div 
                        key={task.task_id} 
                        className="flex items-center justify-between p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                        onClick={() => openTaskDetail(task)}
                        data-testid={`task-card-${task.task_id}`}
                      >
                        <div className="flex items-center gap-4">
                          {getPriorityIcon(task.priority)}
                          <div>
                            <p className="font-medium">{task.title}</p>
                            {task.deadline && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                <Calendar className="w-3 h-3" />
                                Due {format(new Date(task.deadline), "MMM d")}
                              </p>
                            )}
                          </div>
                        </div>
                        <Select 
                          value={task.status} 
                          onValueChange={(value) => handleStatusUpdate(task.task_id, value)}
                        >
                          <SelectTrigger className="w-32" onClick={(e) => e.stopPropagation()} data-testid={`status-select-${task.task_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                    {tasks.filter(t => t.status !== "completed").length === 0 && (
                      <div className="py-12 text-center">
                        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                        <p className="text-muted-foreground">All caught up! No pending tasks.</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Tasks Tab - Calendar View */}
          {activeTab === "tasks" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* View Toggle and Filter */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <Tabs value={calendarView} onValueChange={setCalendarView} className="w-auto">
                  <TabsList>
                    <TabsTrigger value="monthly" className="gap-2" data-testid="view-monthly">
                      <CalendarDays className="w-4 h-4" />
                      Monthly
                    </TabsTrigger>
                    <TabsTrigger value="weekly" className="gap-2" data-testid="view-weekly">
                      <CalendarRange className="w-4 h-4" />
                      Weekly
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                <div className="flex items-center gap-4">
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-40" data-testid="filter-status">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Tasks</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground">{filteredTasks.length} total tasks</span>
                </div>
              </div>

              {/* Monthly Calendar View */}
              {calendarView === "monthly" && (
                <div className="grid lg:grid-cols-[350px_1fr] gap-6">
                  {/* Calendar */}
                  <Card className="shadow-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg font-heading">Select Date</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CalendarComponent
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => date && setSelectedDate(date)}
                        className="rounded-md border"
                        modifiers={{
                          hasTasks: (date) => getTasksForDate(date).length > 0
                        }}
                        modifiersClassNames={{
                          hasTasks: "bg-primary/20 font-bold"
                        }}
                      />
                    </CardContent>
                  </Card>

                  {/* Tasks for selected date */}
                  <Card className="shadow-card">
                    <CardHeader>
                      <CardTitle className="text-lg font-heading flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-primary" />
                        Tasks for {format(selectedDate, "EEEE, MMMM d, yyyy")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-[400px] pr-4">
                        {dayTasks.length > 0 ? (
                          <div className="space-y-3">
                            {dayTasks
                              .filter(task => filterStatus === "all" || task.status === filterStatus)
                              .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""))
                              .map((task) => (
                                <div
                                  key={task.task_id}
                                  className="p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors cursor-pointer border-l-4"
                                  style={{
                                    borderLeftColor: task.priority === "high" ? "#ef4444" : task.priority === "medium" ? "#f59e0b" : "#22c55e"
                                  }}
                                  onClick={() => openTaskDetail(task)}
                                  data-testid={`calendar-task-${task.task_id}`}
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        {task.start_time && (
                                          <span className="text-sm font-mono font-semibold text-primary">
                                            {formatTime(task.start_time)}
                                            {task.duration && ` - ${formatTime(getEndTime(task.start_time, task.duration))}`}
                                          </span>
                                        )}
                                        {task.duration && (
                                          <Badge variant="outline" className="text-xs">
                                            {task.duration >= 60 ? `${task.duration / 60}h` : `${task.duration}m`}
                                          </Badge>
                                        )}
                                      </div>
                                      <h4 className="font-medium text-foreground">{task.title}</h4>
                                      {task.description && (
                                        <p className="text-sm text-muted-foreground line-clamp-1 mt-1">{task.description}</p>
                                      )}
                                    </div>
                                    <div className="flex flex-col items-end gap-2">
                                      <Badge variant="outline" className={cn(
                                        "text-xs",
                                        task.status === "pending" && "status-pending",
                                        task.status === "in_progress" && "status-in_progress",
                                        task.status === "completed" && "status-completed"
                                      )}>
                                        {task.status?.replace("_", " ")}
                                      </Badge>
                                      <Select 
                                        value={task.status} 
                                        onValueChange={(value) => handleStatusUpdate(task.task_id, value)}
                                      >
                                        <SelectTrigger 
                                          className="w-28 h-8 text-xs" 
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="pending">Pending</SelectItem>
                                          <SelectItem value="in_progress">In Progress</SelectItem>
                                          <SelectItem value="completed">Completed</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
                            <Calendar className="w-12 h-12 mb-3 opacity-50" />
                            <p>No tasks scheduled for this day</p>
                          </div>
                        )}
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Weekly Calendar View */}
              {calendarView === "weekly" && (
                <Card className="shadow-card">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-heading">
                        Week of {format(weekStart, "MMM d")} - {format(addDays(weekStart, 6), "MMM d, yyyy")}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setWeekStart(subWeeks(weekStart, 1))}
                          data-testid="prev-week"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
                        >
                          Today
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setWeekStart(addWeeks(weekStart, 1))}
                          data-testid="next-week"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-7 gap-2">
                      {eachDayOfInterval({ start: weekStart, end: addDays(weekStart, 6) }).map((day) => {
                        const dayTasksList = getTasksForDate(day).filter(
                          task => filterStatus === "all" || task.status === filterStatus
                        );
                        const isToday = isSameDay(day, new Date());
                        const isSelected = isSameDay(day, selectedDate);
                        
                        return (
                          <div
                            key={day.toISOString()}
                            className={cn(
                              "min-h-[200px] p-2 rounded-lg border cursor-pointer transition-colors",
                              isToday && "border-primary bg-primary/5",
                              isSelected && "ring-2 ring-primary",
                              !isToday && !isSelected && "hover:bg-muted/50"
                            )}
                            onClick={() => setSelectedDate(day)}
                            data-testid={`week-day-${format(day, "yyyy-MM-dd")}`}
                          >
                            <div className="text-center mb-2">
                              <p className="text-xs text-muted-foreground">{format(day, "EEE")}</p>
                              <p className={cn(
                                "text-lg font-semibold",
                                isToday && "text-primary"
                              )}>
                                {format(day, "d")}
                              </p>
                            </div>
                            <ScrollArea className="h-[150px]">
                              <div className="space-y-1">
                                {dayTasksList
                                  .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""))
                                  .map((task) => (
                                    <div
                                      key={task.task_id}
                                      className={cn(
                                        "p-1.5 rounded text-xs cursor-pointer transition-colors",
                                        task.status === "completed" ? "bg-green-100 text-green-800" :
                                        task.status === "in_progress" ? "bg-blue-100 text-blue-800" :
                                        task.priority === "high" ? "bg-red-100 text-red-800" :
                                        task.priority === "medium" ? "bg-amber-100 text-amber-800" :
                                        "bg-gray-100 text-gray-800"
                                      )}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openTaskDetail(task);
                                      }}
                                    >
                                      {task.start_time && (
                                        <span className="font-mono font-medium">{formatTime(task.start_time).split(" ")[0]} </span>
                                      )}
                                      <span className="line-clamp-1">{task.title}</span>
                                    </div>
                                  ))}
                                {dayTasksList.length === 0 && (
                                  <p className="text-xs text-muted-foreground text-center py-4">No tasks</p>
                                )}
                              </div>
                            </ScrollArea>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Task List (backup view) */}
              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="text-lg font-heading">All My Tasks</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-3">
                      {filteredTasks
                        .sort((a, b) => {
                          if (a.task_date && b.task_date && a.task_date !== b.task_date) {
                            return a.task_date.localeCompare(b.task_date);
                          }
                          return (a.start_time || "").localeCompare(b.start_time || "");
                        })
                        .map((task) => (
                          <div
                            key={task.task_id}
                            className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                            onClick={() => openTaskDetail(task)}
                            data-testid={`task-list-${task.task_id}`}
                          >
                            <div className="flex items-center gap-3">
                              {getPriorityIcon(task.priority)}
                              <div>
                                <p className="font-medium text-sm">{task.title}</p>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  {task.task_date && (
                                    <span>{format(new Date(task.task_date + "T00:00:00"), "MMM d")}</span>
                                  )}
                                  {task.start_time && (
                                    <span>{formatTime(task.start_time)}</span>
                                  )}
                                  {task.duration && (
                                    <span>({task.duration} min)</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <Badge variant="outline" className={cn(
                              "text-xs",
                              task.status === "pending" && "status-pending",
                              task.status === "in_progress" && "status-in_progress",
                              task.status === "completed" && "status-completed"
                            )}>
                              {task.status?.replace("_", " ")}
                            </Badge>
                          </div>
                        ))}
                      {filteredTasks.length === 0 && (
                        <div className="py-8 text-center text-muted-foreground">
                          <ListTodo className="w-10 h-10 mx-auto mb-2 opacity-50" />
                          <p>No tasks found</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* QA Calls Tab (for all employees) */}
          {activeTab === "qa_calls" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <QACallsModule user={user} />
            </motion.div>
          )}
        </div>
      </main>

      {/* Task Detail Dialog */}
      <Dialog open={showTaskDetail} onOpenChange={setShowTaskDetail}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-3">
              {selectedTask && getPriorityIcon(selectedTask.priority)}
              {selectedTask?.title}
            </DialogTitle>
            <DialogDescription>
              {selectedTask?.description || "No description provided"}
            </DialogDescription>
          </DialogHeader>
          
          {selectedTask && (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Task Info */}
              <div className="flex flex-wrap gap-3 py-4 border-b border-border">
                <Badge variant="outline" className={cn(
                  "text-xs",
                  selectedTask.priority === "high" && "priority-high",
                  selectedTask.priority === "medium" && "priority-medium",
                  selectedTask.priority === "low" && "priority-low"
                )}>
                  {selectedTask.priority} priority
                </Badge>
                <Badge variant="outline" className={cn(
                  "text-xs",
                  selectedTask.status === "pending" && "status-pending",
                  selectedTask.status === "in_progress" && "status-in_progress",
                  selectedTask.status === "completed" && "status-completed"
                )}>
                  {selectedTask.status?.replace("_", " ")}
                </Badge>
                {selectedTask.task_date && (
                  <Badge variant="outline" className="text-xs">
                    <Calendar className="w-3 h-3 mr-1" />
                    {format(new Date(selectedTask.task_date + "T00:00:00"), "MMM d, yyyy")}
                  </Badge>
                )}
                {selectedTask.start_time && (
                  <Badge variant="outline" className="text-xs">
                    <Clock className="w-3 h-3 mr-1" />
                    {formatTime(selectedTask.start_time)}
                    {selectedTask.duration && ` - ${formatTime(getEndTime(selectedTask.start_time, selectedTask.duration))}`}
                  </Badge>
                )}
                {selectedTask.duration && (
                  <Badge variant="outline" className="text-xs">
                    {selectedTask.duration >= 60 ? `${selectedTask.duration / 60} hour(s)` : `${selectedTask.duration} min`}
                  </Badge>
                )}
                {selectedTask.deadline && (
                  <Badge variant="outline" className="text-xs bg-amber-50 border-amber-200 text-amber-700">
                    Due {format(new Date(selectedTask.deadline), "MMM d, yyyy")}
                  </Badge>
                )}
                {selectedTask.day_of_week && selectedTask.time_slot && (
                  <Badge variant="outline" className="text-xs">
                    {selectedTask.day_of_week} {selectedTask.time_slot}
                  </Badge>
                )}
              </div>

              {/* Status Update */}
              <div className="py-4 border-b border-border">
                <label className="text-sm font-medium mb-2 block">Update Status</label>
                <Select 
                  value={selectedTask.status} 
                  onValueChange={(value) => handleStatusUpdate(selectedTask.task_id, value)}
                >
                  <SelectTrigger className="w-48" data-testid="detail-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Notes Section */}
              <div className="flex-1 overflow-hidden flex flex-col py-4">
                <h4 className="text-sm font-medium mb-3">Notes & Updates</h4>
                
                <ScrollArea className="flex-1 pr-4 -mr-4">
                  <div className="space-y-3">
                    {selectedTask.notes?.length > 0 ? (
                      selectedTask.notes.map((note, index) => (
                        <div key={index} className="p-3 rounded-lg bg-muted/50">
                          <div className="flex items-center gap-2 mb-2">
                            <Avatar className="w-6 h-6">
                              <AvatarFallback className="text-xs">{note.user_name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <span className="text-sm font-medium">{note.user_name}</span>
                            <span className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(note.timestamp), { addSuffix: true })}
                            </span>
                          </div>
                          <p className="text-sm text-foreground">{note.text}</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">No notes yet</p>
                    )}
                  </div>
                </ScrollArea>

                {/* Add Note */}
                <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                  <Textarea
                    placeholder="Add a note..."
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    className="min-h-[60px] resize-none"
                    data-testid="note-input"
                  />
                  <Button 
                    size="icon" 
                    onClick={handleAddNote}
                    disabled={!noteText.trim()}
                    data-testid="add-note-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
